/**
 * The result overlay's copy, as pure functions (design 05 "Result · won" and 05b
 * "Result · variants").
 *
 * Kept out of the component so the four outcomes are unit-testable without
 * rendering anything — the card itself only formats what these return.
 */
import type {GameResult} from '@/Store/liveGame/state';

export type Outcome = GameResult['outcome'];

/** The Thunder word across the top of the card. */
export const outcomeWord = (outcome: Outcome): string => {
  switch (outcome) {
    case 'Win':
      return 'YOU WON';
    case 'Loss':
      return 'YOU LOST';
    case 'Draw':
      return 'DRAW';
    default:
      return 'ABORTED';
  }
};

/** "1 – 0" / "0 – 1" / "½ – ½"; an aborted game has no score. */
export const scoreFor = (outcome: Outcome): string => {
  switch (outcome) {
    case 'Win':
      return '1 – 0';
    case 'Loss':
      return '0 – 1';
    case 'Draw':
      return '½ – ½';
    default:
      return '';
  }
};

/** The server's endType, in the design's sentence case. */
export const endTypeLabel = (endType: string): string => {
  switch (endType) {
    case 'checkmate':
      return 'Checkmate';
    case 'resign':
      return 'Resigned';
    case 'stalemate':
      return 'Stalemate';
    case 'repetition':
      return 'Repetition';
    case 'insufficient':
      return 'Insufficient material';
    case 'draw':
      return 'Draw agreed';
    case 'timeout':
    case 'time_up':
      return 'Time out';
    case 'abort':
      return 'No first move';
    default:
      return endType ? endType[0].toUpperCase() + endType.slice(1) : '';
  }
};

/** "Checkmate · 1 – 0" — the small line under the message, from a score. */
export const resultLineFor = (endType: string, score: string): string => {
  const label = endTypeLabel(endType);
  if (!label) {
    return score;
  }
  return score ? `${label} · ${score}` : label;
};

/** "Checkmate · 1 – 0" — the small line under the message (my own result). */
export const resultLine = (endType: string, outcome: Outcome): string =>
  resultLineFor(endType, scoreFor(outcome));

/** the watch-end reducer's outcome: by COLOUR, since a spectator has no side */
export type WatchOutcome = 'white' | 'black' | 'draw' | 'abort' | null;

/**
 * Fix AF (owner 2026-10-07, "in a watched Swiss game it shows You Won"): the
 * spectator card's Thunder word names the WINNER'S COLOUR, never "you".
 */
export const spectatorWord = (outcome: WatchOutcome): string => {
  switch (outcome) {
    case 'white':
      return 'WHITE WON';
    case 'black':
      return 'BLACK WON';
    case 'draw':
      return 'DRAW';
    case 'abort':
      return 'ABORTED';
    default:
      return 'GAME OVER';
  }
};

/** "1 – 0" / "0 – 1" / "½ – ½" by colour; an aborted game has no score. */
export const spectatorScore = (outcome: WatchOutcome): string => {
  switch (outcome) {
    case 'white':
      return '1 – 0';
    case 'black':
      return '0 – 1';
    case 'draw':
      return '½ – ½';
    default:
      return '';
  }
};

/**
 * The spectator card's message: the winner's NAME when the seat carries one
 * ("Bala won the match!"), else the reducer's own colour sentence.
 */
export const spectatorMessage = (
  outcome: WatchOutcome,
  message: string,
  names: {white: string; black: string},
): string => {
  if (outcome !== 'white' && outcome !== 'black') {
    return message;
  }
  const name = names[outcome].trim();
  return name ? `${name} won the match!` : message;
};

/**
 * The signed rating delta. The design uses a real minus sign (U+2212), not a
 * hyphen, because the figures are Thunder tabular numerals.
 */
export const formatDelta = (delta: number): string => {
  const rounded = Math.round(delta);
  if (rounded > 0) {
    return `+${rounded}`;
  }
  if (rounded < 0) {
    return `−${Math.abs(rounded)}`;
  }
  return '0';
};

export type DeltaTone = 'pos' | 'danger' | 'muted';

export const deltaTone = (delta: number): DeltaTone => {
  const rounded = Math.round(delta);
  if (rounded > 0) {
    return 'pos';
  }
  if (rounded < 0) {
    return 'danger';
  }
  return 'muted';
};
